import { isPlatformBrowser } from '@angular/common';
import {
  computed,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { LocalStorageService } from '../../../shared/local-storage/local-storage.service';
import {
  COOKIE_CONSENT_STORAGE_KEY,
  CookieConsentLevel,
  CookieConsentRecord,
} from '../domain/cookie-consent.types';

type GtagFn = (
  command: 'consent',
  action: 'default' | 'update',
  params: Record<string, string>,
) => void;

interface WindowWithGtag extends Window {
  gtag?: GtagFn;
}

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  readonly hasConsent = signal<boolean>(false);
  readonly consentLevel = signal<CookieConsentLevel | null>(null);
  readonly showDialog = computed(() => this.isBrowser && !this.hasConsent());

  constructor() {
    this.initConsent();
  }

  private initConsent(): void {
    if (!this.isBrowser) {
      return;
    }

    try {
      const raw = this.localStorageService.get(COOKIE_CONSENT_STORAGE_KEY);
      if (raw) {
        const record = JSON.parse(raw) as Partial<CookieConsentRecord>;
        if (
          record &&
          (record.level === 'all' || record.level === 'essential')
        ) {
          this.consentLevel.set(record.level);
          this.hasConsent.set(true);
          if (record.level === 'all') {
            this.updateGoogleConsent('granted');
          }
        }
      }
    } catch {
      // Malformed storage value, maintain unconsented state
    }
  }

  acceptAll(): void {
    const record: CookieConsentRecord = {
      level: 'all',
      updatedAt: new Date().toISOString(),
    };
    this.localStorageService.set(COOKIE_CONSENT_STORAGE_KEY, record);
    this.consentLevel.set('all');
    this.hasConsent.set(true);
    this.updateGoogleConsent('granted');
  }

  acceptEssential(): void {
    const record: CookieConsentRecord = {
      level: 'essential',
      updatedAt: new Date().toISOString(),
    };
    this.localStorageService.set(COOKIE_CONSENT_STORAGE_KEY, record);
    this.consentLevel.set('essential');
    this.hasConsent.set(true);
    this.updateGoogleConsent('denied');
  }

  private updateGoogleConsent(status: 'granted' | 'denied'): void {
    if (!this.isBrowser) {
      return;
    }

    const win = window as unknown as WindowWithGtag;
    if (typeof win.gtag === 'function') {
      win.gtag('consent', 'update', {
        analytics_storage: status,
        ad_storage: status,
        ad_user_data: status,
        ad_personalization: status,
      });
    }
  }
}
